import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import LanguageLayout from "./components/layout/LanguageLayout";
import ChatBubble from "./components/common/ChatBubble";

// Critical pages loaded eagerly for fast initial load
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy load non-critical pages
const Features = lazy(() => import("./pages/Features"));
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Demo = lazy(() => import("./pages/Demo"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Careers = lazy(() => import("./pages/Careers"));
const ApiDocumentation = lazy(() => import("./pages/ApiDocumentation"));
const BusinessCases = lazy(() => import("./pages/BusinessCases"));
const MeetingBooking = lazy(() => import("./pages/MeetingBooking"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Lazy load admin pages (not needed for public visitors)
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const ContentPlan = lazy(() => import("./pages/admin/ContentPlan"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const CronJobs = lazy(() => import("./pages/admin/CronJobs"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const CompetitorInsights = lazy(() => import("./pages/admin/CompetitorInsights"));
const KeywordGapAnalysis = lazy(() => import("./pages/admin/KeywordGapAnalysis"));
const BusinessSettings = lazy(() => import("./pages/admin/settings/BusinessSettings"));
const CompetitorSettings = lazy(() => import("./pages/admin/settings/CompetitorSettings"));
const ArticleSettings = lazy(() => import("./pages/admin/settings/ArticleSettings"));
const PublishingSettings = lazy(() => import("./pages/admin/settings/PublishingSettings"));
const LinkingSettings = lazy(() => import("./pages/admin/settings/LinkingSettings"));
const VisualSettings = lazy(() => import("./pages/admin/settings/VisualSettings"));
const VideoSettings = lazy(() => import("./pages/admin/settings/VideoSettings"));
const SocialSharingSettings = lazy(() => import("./pages/admin/settings/SocialSharingSettings"));
const AgentDetail = lazy(() => import("./pages/admin/agents/AgentDetail"));
const SEOOptimizer = lazy(() => import("./pages/admin/agents/SEOOptimizer"));
const TranslatorAgent = lazy(() => import("./pages/admin/agents/TranslatorAgent"));
const PipelineStatus = lazy(() => import("./pages/admin/PipelineStatus"));
const VideoAuditLog = lazy(() => import("./pages/admin/VideoAuditLog"));
const TrustSignals = lazy(() => import("./pages/admin/TrustSignals"));
const Responses = lazy(() => import("./pages/admin/Responses"));
const Followers = lazy(() => import("./pages/admin/Followers"));
const ProtectedRoute = lazy(() => import("./components/admin/ProtectedRoute"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Auth route */}
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />


                  {/* Admin routes (protected) */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requiredRole="admin">
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Dashboard />} />
                    <Route path="pipeline" element={<PipelineStatus />} />
                    <Route path="content-plan" element={<ContentPlan />} />
                    <Route path="trust-signals" element={<TrustSignals />} />
                    <Route path="competitor-insights" element={<CompetitorInsights />} />
                    <Route path="keyword-gaps" element={<KeywordGapAnalysis />} />
                    <Route path="analytics" element={<Analytics />} />
                    <Route path="cron" element={<CronJobs />} />
                    <Route path="video-audit" element={<VideoAuditLog />} />
                    <Route path="responses" element={<Responses />} />
                    <Route path="followers" element={<Followers />} />
                    <Route path="agents/seo" element={<SEOOptimizer />} />
                    <Route path="agents/translator" element={<TranslatorAgent />} />
                    <Route path="agents/:agentId" element={<AgentDetail />} />
                    <Route path="settings" element={<Settings />}>
                      <Route path="business" element={<BusinessSettings />} />
                      <Route path="competitors" element={<CompetitorSettings />} />
                      <Route path="generation" element={<ArticleSettings />} />
                      <Route path="publishing" element={<PublishingSettings />} />
                      <Route path="linking" element={<LinkingSettings />} />
                      <Route path="visuals" element={<VisualSettings />} />
                      <Route path="video" element={<VideoSettings />} />
                      <Route path="sharing" element={<SocialSharingSettings />} />
                    </Route>
                  </Route>

                  {/* Language-prefixed routes */}
                  <Route path="/:lang" element={<LanguageLayout />}>
                    <Route index element={<Index />} />
                    <Route path="features" element={<Features />} />
                    <Route path="pricing" element={<Pricing />} />
                    <Route path="about" element={<About />} />
                    <Route path="contact" element={<Contact />} />
                    <Route path="demo" element={<Demo />} />
                    <Route path="blog" element={<Blog />} />
                    <Route path="blog/:slug" element={<BlogPost />} />
                    <Route path="privacy" element={<Privacy />} />
                    <Route path="terms" element={<Terms />} />
                    <Route path="cookies" element={<Cookies />} />
                    <Route path="careers" element={<Careers />} />
                    <Route path="api-documentation" element={<ApiDocumentation />} />
                    <Route path="business-cases" element={<BusinessCases />} />
                    <Route path="book-meeting" element={<MeetingBooking />} />
                    <Route path="faq" element={<FAQ />} />
                  </Route>
                  
                  {/* Non-prefixed routes (default English) */}
                  <Route path="/" element={<Index />} />
                  <Route path="/features" element={<Features />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/demo" element={<Demo />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/cookies" element={<Cookies />} />
                  <Route path="/careers" element={<Careers />} />
                  <Route path="/api-documentation" element={<ApiDocumentation />} />
                  <Route path="/business-cases" element={<BusinessCases />} />
                  <Route path="/book-meeting" element={<MeetingBooking />} />
                  <Route path="/faq" element={<FAQ />} />
                  
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
            <ChatBubble />
          </TooltipProvider>
        </LanguageProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
